import { Card, CardContent, cn } from '@sunstone/ui';
import { Activity, AlertTriangle, CheckCircle2, Layers, Server } from 'lucide-react';
import { Link } from 'react-router';

function StatCard({
  value,
  label,
  sublabel,
  accent,
  icon: Icon,
  href,
}: {
  value: string;
  label: string;
  sublabel?: string;
  accent: string;
  icon: React.ElementType;
  href?: string;
}) {
  const content = (
    <Card
      className={cn(
        'relative overflow-hidden transition-shadow hover:shadow-md',
        href && 'cursor-pointer',
      )}
    >
      <span className={`absolute top-0 left-0 h-0.5 w-full ${accent}`} />
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums">{value}</p>
            {sublabel && <p className="mt-0.5 text-xs text-muted-foreground">{sublabel}</p>}
          </div>
          <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
        </div>
      </CardContent>
    </Card>
  );
  return href ? (
    <Link to={href} className="block">
      {content}
    </Link>
  ) : (
    content
  );
}

interface WorkspaceSummaryCardsProps {
  totalWorkspaces: number;
  healthyWorkspaces: number;
  atRiskCount: number;
  deploymentCount: number;
  tenantCount?: number;
  isLoading: boolean;
  atRiskLoading?: boolean;
  showTenants?: boolean;
  isPlatformAdmin?: boolean;
}

export function WorkspaceSummaryCards({
  totalWorkspaces,
  healthyWorkspaces,
  atRiskCount,
  deploymentCount,
  tenantCount,
  isLoading,
  atRiskLoading = false,
  showTenants = false,
  isPlatformAdmin = false,
}: WorkspaceSummaryCardsProps) {
  const dash = '—';
  const colCount = showTenants ? 4 : 3;

  // A tenant-less Admin sees all tenants platform-wide; a partner Admin sees only their customers
  const tenantLabel = 'Customers';
  const tenantSublabel = isPlatformAdmin ? 'Partners & customers' : 'Under your account';

  return (
    <div
      className="grid gap-4 grid-cols-2"
      style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
    >
      {showTenants && (
        <StatCard
          value={isLoading ? dash : String(tenantCount ?? 0)}
          label={tenantLabel}
          sublabel={tenantSublabel}
          accent="bg-slate-400"
          icon={Layers}
        />
      )}
      <StatCard
        value={isLoading ? dash : String(deploymentCount)}
        label="Deployments"
        sublabel="Total in your fleet"
        accent="bg-indigo-500"
        icon={Activity}
        href="/deployments"
      />
      <StatCard
        value={isLoading ? dash : String(healthyWorkspaces)}
        label="Healthy"
        sublabel={isLoading ? undefined : `of ${totalWorkspaces} workspaces`}
        accent="bg-[#00D4A4]"
        icon={CheckCircle2}
        href="/workspaces"
      />
      {atRiskLoading ? (
        <StatCard
          value={dash}
          label="At risk"
          sublabel="Checking…"
          accent="bg-rose-500"
          icon={AlertTriangle}
        />
      ) : atRiskCount > 0 ? (
        <StatCard
          value={String(atRiskCount)}
          label="At risk"
          sublabel="Need attention"
          accent="bg-rose-500"
          icon={AlertTriangle}
          href="/workspaces"
        />
      ) : (
        <StatCard
          value={isLoading ? dash : String(totalWorkspaces)}
          label="Workspaces"
          sublabel="Total fleet"
          accent="bg-slate-400"
          icon={Server}
          href="/workspaces"
        />
      )}
    </div>
  );
}
